import React, { useCallback, useEffect, useState } from "react";
import {
  append,
  find,
  length,
  lensPath,
  lensProp,
  mergeLeft,
  move,
  not,
  over,
  propEq,
  remove,
} from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { set } from "ramda";
import Grid from "@mui/material/Grid";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { List, ListItem, ListItemText } from "@mui/material";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Typography from "@mui/material/Typography";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

/**
 * Shared edit dialog for Sub Projects and Custom Dropdown dropdown options
 *
 * | STIP name          | Manager Panel UI name |
 * | ------------------ | --------------------- |
 * | "Sub Projects"     | "Custom Dropdown 1"   |
 * | "Custom Dropdown"  | "Custom Dropdown 2"   |
 *
 * @param {Object} fieldNames - field names for dropdown option
 * @param {'subProjectsLabel' | 'customDropdownLabel'}
 * @param {'subProjectsRequired' | 'customDropdownRequired'}
 * @param {'sub_projects' | 'custom_dropdown'} fieldOptionProp - name of the field option prop used by libraries-share-ui
 * @param {'subProjects' | 'customDropdownItems'} dropdownItemsProp - name of the field on the project that stores the options for each custom dropdown
 * @param {'subProjectName' | 'customDropdownName'} dropdownItemNameProp - the 'name' of the dropdown option. See table columns for stip.sub_project and stip.custom_dropdown
 * @param {'subProjectId' | 'customDropdownId'} dropdownItemIdProp - the 'id' of the dropdown option. See table columns for stip.sub_project and stip.custom_dropdown
 */
const CustomDropdownDialog = ({
  project,
  fieldNames,
  fieldOptions,
  fieldOptionProp,
  dropdownItemsProp,
  dropdownItemNameProp,
  dropdownItemIdProp,
  open,
  onCancel,
  onApply,
}) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    [fieldNames.label]: null,
    [fieldNames.required]: false,
  });
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (notBlank(fieldOptions[fieldOptionProp])) {
      setFields({
        [fieldNames.label]: fieldOptions[fieldOptionProp].fieldLabel || null,
        [fieldNames.required]: fieldOptions[fieldOptionProp].required || false,
      });
    }
    if (notBlank(project[dropdownItemsProp])) {
      setOptions(project[dropdownItemsProp]);
    }
  }, [project, fieldNames, fieldOptions, fieldOptionProp, dropdownItemsProp]);

  const [optionValue, setOptionValue] = useState(null);
  const [error, setError] = useState(null);

  const handleAddOptionClick = useCallback(() => {
    const existing = find(propEq(dropdownItemNameProp, optionValue), options);
    if (notBlank(existing)) {
      setError(
        "A Dropdown Item Option with the specified value already exists.",
      );
    } else {
      setOptions(prev =>
        mapIndexed(
          (val, idx) => set(lensProp("displayOrder"), idx, val),
          append({ [dropdownItemNameProp]: optionValue, active: true }, prev),
        ),
      );
      setOptionValue("");
      setError(null);
    }
  }, [dropdownItemNameProp, optionValue, options]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleToggleVisible = idx => {
    setOptions(prev => {
      return over(lensPath([idx, "active"]), not, prev);
    });
  };

  const handleDeleteOption = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        remove(idx, 1, prev),
      ),
    );
  };

  const handleOptionUp = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        move(idx, idx - 1, prev),
      ),
    );
  };

  const handleOptionDown = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        move(idx, idx + 1, prev),
      ),
    );
  };

  const handleApply = async () => {
    onApply(mergeLeft(fields, { [dropdownItemsProp]: options }));
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">
        Edit Custom Dropdown Items
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter properties for the Custom Dropdown field.
        </DialogContentText>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <TextField
              id="custom-dropdown-label"
              fullWidth
              label="Custom Dropdown Label"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields[fieldNames.label]}
              onChange={handleTextChange([fieldNames.label])}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields[fieldNames.required]}
                  onChange={handleCheckboxChange(fieldNames.required)}
                  name="custom-dropdown-required"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Custom Dropdown Required"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5">Options</Typography>
            <List dense>
              {mapIndexed(
                (opt, idx) => (
                  <ListItem
                    divider
                    key={opt[dropdownItemIdProp] || opt[dropdownItemNameProp]}
                  >
                    <ListItemText primary={opt[dropdownItemNameProp]} />
                    <ListItemSecondaryAction>
                      {isBlank(opt[dropdownItemIdProp]) && (
                        <ConfirmButton
                          handleDelete={() => handleDeleteOption(idx)}
                          dialogTitle="Remove Custom Dropdown"
                          dialogBody="Are you sure you want to remove this custom dropdown?"
                        />
                      )}

                      {opt.active ? (
                        <Tooltip title="Mark Hidden">
                          <IconButton
                            onClick={() => handleToggleVisible(idx)}
                            edge="end"
                            aria-label="hide"
                            size="large"
                          >
                            <VisibilityIcon color="primary" fontSize="large" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Mark Visible">
                          <IconButton
                            onClick={() => handleToggleVisible(idx)}
                            edge="end"
                            aria-label="show"
                            size="large"
                          >
                            <VisibilityOffIcon color="error" fontSize="large" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        onClick={() => handleOptionUp(idx)}
                        disabled={idx === 0}
                        edge="end"
                        aria-label="move up"
                        size="large"
                      >
                        <Tooltip title="Move Up">
                          <ArrowDropUpIcon
                            color={idx === 0 ? "disabled" : "primary"}
                            fontSize="large"
                          />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        onClick={() => handleOptionDown(idx)}
                        disabled={idx === length(options) - 1}
                        edge="end"
                        aria-label="move down"
                        size="large"
                      >
                        <Tooltip title="Move Down">
                          <ArrowDropDownIcon
                            color={
                              idx === length(options) - 1
                                ? "disabled"
                                : "primary"
                            }
                            fontSize="large"
                          />
                        </Tooltip>
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ),
                options,
              )}
            </List>
          </Grid>
          <Grid item xs>
            <TextField
              id="option-value"
              fullWidth
              label="New Custom Download Option"
              InputProps={{
                style: { fontSize: "1.8rem" },
              }}
              value={optionValue}
              onChange={event => setOptionValue(event.target.value)}
              error={notBlank(error)}
              helperText={error}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={handleAddOptionClick}
              disabled={isBlank(optionValue)}
              aria-label="add option"
            >
              Add Option
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDropdownDialog;
